import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Mic, Sparkles, Target } from "lucide-react";
import { CharacterById } from "@/components/LanguageCharacters";
import { useT } from "@/lib/i18n";

export default function ConversationPicker() {
  const t = useT();
  const { lang } = useParams();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const character = localStorage.getItem("lang_guide") || "owl";

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/languages/conversation/scenarios`, { params: { lang } });
        setScenarios(r.data.scenarios);
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudieron cargar los escenarios");
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  const start = async (scenarioId) => {
    setStarting(scenarioId);
    try {
      const r = await api.post(`/languages/conversation/start`, {
        lang,
        scenario_id: scenarioId,
        character,
      });
      navigate(`/idiomas/conversar/${lang}/sesion/${r.data.session_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar");
      setStarting(null);
    }
  };

  if (loading) return <div className="text-[#374151]">{t("common.loading")}</div>;

  const langLabel = { en: t("onb.opt.lang.en").replace("🇺🇸 ", ""), fr: t("onb.opt.lang.fr").replace("🇫🇷 ", ""), es: t("onb.opt.lang.es").replace("🇪🇸 ", "") }[lang] || lang;

  return (
    <div className="space-y-8" data-testid="conversation-picker">
      <button
        onClick={() => navigate(`/idiomas/${lang}`)}
        className="text-sm text-[#6B7280] hover:text-[#1B2E7A] inline-flex items-center gap-1"
        data-testid="back-to-map"
      >
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </button>

      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-[#F4B81F] mb-2 inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> {t("conv.picker.eyebrow")}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A] leading-tight">
            {t("conv.picker.title")}
          </h1>
          <p className="text-[#6B7280] mt-3 max-w-xl">
            {t("conv.picker.subtitle", { lang: langLabel })}
          </p>
          <div className="mt-4 flex items-center gap-3 text-xs text-[#6B7280]">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F3F4F6]">
              <Mic className="h-3.5 w-3.5" /> {t("conv.picker.tag_browser")}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F3F4F6]">
              <Target className="h-3.5 w-3.5" /> {t("conv.picker.tag_pts")}
            </span>
          </div>
        </div>
        <CharacterById id={character} size={140} mood="cheer" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {scenarios.map((s) => (
          <button
            key={s.id}
            disabled={starting === s.id}
            onClick={() => start(s.id)}
            className="text-left bg-white rounded-2xl border border-[#E5E7EB] p-6 hover:-translate-y-0.5 hover:shadow-md transition-all disabled:opacity-60"
            data-testid={`scenario-${s.id}`}
            style={{ borderLeft: `4px solid ${s.color}` }}
          >
            <div className="flex items-start justify-between mb-3">
              <span className="text-4xl" aria-hidden>{s.icon}</span>
              <ArrowRight className="h-5 w-5 text-[#6B7280] mt-2" />
            </div>
            <h3 className="font-heading text-xl text-[#1A1A1A] leading-tight">{s.title}</h3>
            <p className="text-sm text-[#6B7280] mt-1">{s.subtitle}</p>
            {s.goals && s.goals.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-[#6B7280]">
                {s.goals.slice(0, 3).map((g, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-[#1B2E7A] mt-0.5">•</span> {g}
                  </li>
                ))}
              </ul>
            )}
            {starting === s.id && (
              <p className="mt-3 text-xs text-[#B45309]">{t("conv.picker.starting")}</p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function labelLang(code) {
  return { en: "inglés", fr: "francés", es: "español" }[code] || code;
}
